import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery } from 'urql';
import { ChevronDown, Search } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import PageHeader from '@/components/Layout/PageHeader.jsx';
import StickyActions from '@/components/StickyActions.jsx';
import Field from '@/pages/Guias/components/Field.jsx';
import {
  SECURITY_ROLE_CREATE_QUERY,
  SECURITY_ROLE_DETAIL_QUERY,
  SECURITY_ROLE_SAVE_MUTATION,
} from './gql.js';

function groupPermissions(permissions = [], search = '') {
  const normalizedSearch = search.trim().toLowerCase();
  return permissions.reduce((acc, permission) => {
    const haystack = `${permission.code} ${permission.name} ${permission.description || ''}`.toLowerCase();
    if (normalizedSearch && !haystack.includes(normalizedSearch)) {
      return acc;
    }
    if (!acc[permission.module]) {
      acc[permission.module] = [];
    }
    acc[permission.module].push(permission);
    return acc;
  }, {});
}

export default function RolFormPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);
  const [search, setSearch] = useState('');
  const [{ data, fetching, error }] = useQuery({
    query: isEdit ? SECURITY_ROLE_DETAIL_QUERY : SECURITY_ROLE_CREATE_QUERY,
    variables: isEdit ? { id } : {},
  });
  const [, saveRole] = useMutation(SECURITY_ROLE_SAVE_MUTATION);

  const role = isEdit ? data?.security_role : null;
  const catalog = data?.security_catalog?.permissions || [];
  const [form, setForm] = useState({
    codigo: '',
    nombre: '',
    descripcion: '',
    nivel: 100,
    tipoRol: 'operativo',
    scopeTipo: 'TENANT',
    activo: true,
    permissionCodes: [],
  });

  useEffect(() => {
    if (!role) return;
    setForm({
      codigo: role.codigo || '',
      nombre: role.nombre || '',
      descripcion: role.descripcion || '',
      nivel: role.nivel || 100,
      tipoRol: role.tipoRol || 'operativo',
      scopeTipo: role.scopeTipo || 'TENANT',
      activo: role.activo ?? true,
      permissionCodes: (role.permisos || []).map((permission) => permission.code),
    });
  }, [role]);

  const groupedPermissions = useMemo(
    () => groupPermissions(catalog, search),
    [catalog, search],
  );

  const selectedPermissions = useMemo(
    () => catalog.filter((permission) => form.permissionCodes.includes(permission.code)),
    [catalog, form.permissionCodes],
  );

  const handlePermissionToggle = (permissionCode) => {
    setForm((current) => ({
      ...current,
      permissionCodes: current.permissionCodes.includes(permissionCode)
        ? current.permissionCodes.filter((code) => code !== permissionCode)
        : [...current.permissionCodes, permissionCode],
    }));
  };

  const handleModuleToggle = (moduleName, enabled) => {
    const moduleCodes = (groupedPermissions[moduleName] || []).map((permission) => permission.code);
    setForm((current) => {
      const next = new Set(current.permissionCodes);
      moduleCodes.forEach((code) => {
        if (enabled) next.add(code);
        else next.delete(code);
      });
      return { ...current, permissionCodes: Array.from(next) };
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const response = await saveRole({
      input: {
        id,
        codigo: form.codigo,
        nombre: form.nombre,
        descripcion: form.descripcion,
        nivel: Number(form.nivel),
        tipoRol: form.tipoRol,
        scopeTipo: form.scopeTipo,
        activo: form.activo,
        permissionCodes: form.permissionCodes,
      },
    });

    if (!response.error) {
      navigate('/seguridad/roles');
    }
  };

  return (
    <div className="min-h-screen bg-[rgb(var(--bg))] pb-20">
      <form onSubmit={handleSubmit} className="mx-auto flex w-full max-w-[1400px] flex-col gap-6 px-6 py-10">
        <PageHeader
          title={isEdit ? 'Editar rol' : 'Nuevo rol'}
          subtitle="Definí jerarquía, scope, descripción funcional y la matriz completa de permisos por módulo."
        />

        <section className="grid gap-6 xl:grid-cols-[1fr_320px]">
          <div className="card rounded-2xl p-6">
            <div className="grid grid-cols-12 gap-4">
              <Field label="Código" htmlFor="codigo" required className="col-span-12 md:col-span-3">
                <input id="codigo" value={form.codigo} onChange={(event) => setForm((current) => ({ ...current, codigo: event.target.value }))} className="w-full rounded-xl border border-[rgba(var(--border),0.65)] bg-[rgba(var(--panel),0.95)] px-3 py-2.5 text-sm text-[rgb(var(--fg))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]" />
              </Field>
              <Field label="Nombre" htmlFor="nombre" required className="col-span-12 md:col-span-5">
                <input id="nombre" value={form.nombre} onChange={(event) => setForm((current) => ({ ...current, nombre: event.target.value }))} className="w-full rounded-xl border border-[rgba(var(--border),0.65)] bg-[rgba(var(--panel),0.95)] px-3 py-2.5 text-sm text-[rgb(var(--fg))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]" />
              </Field>
              <Field label="Nivel jerárquico" htmlFor="nivel" required className="col-span-12 md:col-span-2">
                <input id="nivel" type="number" min="0" value={form.nivel} onChange={(event) => setForm((current) => ({ ...current, nivel: Number(event.target.value) }))} className="w-full rounded-xl border border-[rgba(var(--border),0.65)] bg-[rgba(var(--panel),0.95)] px-3 py-2.5 text-sm text-[rgb(var(--fg))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]" />
              </Field>
              <Field label="Estado" htmlFor="activo" className="col-span-12 md:col-span-2">
                <select id="activo" value={form.activo ? '1' : '0'} onChange={(event) => setForm((current) => ({ ...current, activo: event.target.value === '1' }))} className="w-full rounded-xl border border-[rgba(var(--border),0.65)] bg-[rgba(var(--panel),0.95)] px-3 py-2.5 text-sm text-[rgb(var(--fg))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]">
                  <option value="1">Activo</option>
                  <option value="0">Inactivo</option>
                </select>
              </Field>
              <Field label="Tipo de rol" htmlFor="tipoRol" className="col-span-12 md:col-span-3">
                <select id="tipoRol" value={form.tipoRol} onChange={(event) => setForm((current) => ({ ...current, tipoRol: event.target.value }))} className="w-full rounded-xl border border-[rgba(var(--border),0.65)] bg-[rgba(var(--panel),0.95)] px-3 py-2.5 text-sm text-[rgb(var(--fg))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]">
                  <option value="operativo">Operativo</option>
                  <option value="supervision">Supervisión</option>
                  <option value="administrativo">Administrativo</option>
                  <option value="gerencial">Gerencial</option>
                </select>
              </Field>
              <Field label="Scope" htmlFor="scopeTipo" className="col-span-12 md:col-span-3">
                <select id="scopeTipo" value={form.scopeTipo} onChange={(event) => setForm((current) => ({ ...current, scopeTipo: event.target.value }))} className="w-full rounded-xl border border-[rgba(var(--border),0.65)] bg-[rgba(var(--panel),0.95)] px-3 py-2.5 text-sm text-[rgb(var(--fg))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]">
                  <option value="TENANT">Tenant</option>
                  <option value="SUCURSAL">Sucursal</option>
                  <option value="MULTISUCURSAL">Multisucursal</option>
                  <option value="GLOBAL">Global</option>
                </select>
              </Field>
              <Field label="Descripción funcional" htmlFor="descripcion" className="col-span-12 md:col-span-6">
                <textarea id="descripcion" rows="3" value={form.descripcion} onChange={(event) => setForm((current) => ({ ...current, descripcion: event.target.value }))} className="w-full rounded-xl border border-[rgba(var(--border),0.65)] bg-[rgba(var(--panel),0.95)] px-3 py-2.5 text-sm text-[rgb(var(--fg))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]" />
              </Field>
            </div>
          </div>

          <aside className="card rounded-2xl p-6">
            <h2 className="text-sm font-semibold uppercase tracking-[0.2em] text-[rgb(var(--muted))]">Impacto</h2>
            <div className="mt-4 space-y-3 text-sm text-[rgba(var(--fg),0.8)]">
              <p><strong className="text-[rgb(var(--fg))]">Nivel {form.nivel}</strong>: define precedencia para administración y grants críticos.</p>
              <p><strong className="text-[rgb(var(--fg))]">{form.scopeTipo}</strong>: determina si el rol se piensa para tenant completo o por sucursal.</p>
              <p><strong className="text-[rgb(var(--fg))]">{selectedPermissions.length}</strong> permisos seleccionados.</p>
              {isEdit ? <p>Usuarios afectados actualmente: {role?.userCount || 0}</p> : null}
            </div>
          </aside>
        </section>

        <section className="card rounded-2xl p-6">
          <div className="grid gap-4 lg:grid-cols-[1fr_320px]">
            <div>
              <label className="relative block">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[rgb(var(--muted))]" />
                <input
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="Buscar permisos por código, módulo o descripción"
                  className="w-full rounded-xl border border-[rgba(var(--border),0.65)] bg-[rgba(var(--panel),0.95)] py-3 pl-10 pr-4 text-sm text-[rgb(var(--fg))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
                />
              </label>

              <div className="mt-5 space-y-4">
                {fetching ? <p className="text-sm text-[rgb(var(--muted))]">Cargando catálogo…</p> : null}
                {error ? <p className="text-sm text-red-500">{error.message}</p> : null}
                {Object.entries(groupedPermissions).map(([moduleName, permissions]) => {
                  const allSelected = permissions.every((permission) => form.permissionCodes.includes(permission.code));
                  return (
                    <details key={moduleName} open className="rounded-2xl border border-[rgba(var(--border),0.4)] bg-[rgba(var(--panel),0.75)]">
                      <summary className="flex cursor-pointer list-none items-center justify-between gap-3 px-4 py-3">
                        <div>
                          <h3 className="text-sm font-semibold capitalize text-[rgb(var(--fg))]">{moduleName}</h3>
                          <p className="text-xs text-[rgb(var(--muted))]">{permissions.length} permisos detectados</p>
                        </div>
                        <div className="flex items-center gap-3">
                          <button
                            type="button"
                            onClick={(event) => {
                              event.preventDefault();
                              handleModuleToggle(moduleName, !allSelected);
                            }}
                            className="rounded-xl border border-[rgba(var(--border),0.6)] px-3 py-1.5 text-xs font-semibold text-[rgb(var(--fg))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
                          >
                            {allSelected ? 'Quitar módulo' : 'Seleccionar módulo'}
                          </button>
                          <ChevronDown className="h-4 w-4 text-[rgb(var(--muted))]" />
                        </div>
                      </summary>
                      <div className="grid grid-cols-1 gap-3 border-t border-[rgba(var(--border),0.35)] px-4 py-4 lg:grid-cols-2">
                        {permissions.map((permission) => (
                          <label key={permission.code} className="flex gap-3 rounded-2xl border border-[rgba(var(--border),0.35)] bg-[rgba(var(--subpanel),0.62)] p-3">
                            <input
                              type="checkbox"
                              checked={form.permissionCodes.includes(permission.code)}
                              onChange={() => handlePermissionToggle(permission.code)}
                              className="mt-1 h-4 w-4 rounded border-[rgba(var(--border),0.8)] text-[rgb(var(--brand))] focus:ring-[rgb(var(--ring))]"
                            />
                            <div className="space-y-1">
                              <div className="flex flex-wrap items-center gap-2">
                                <span className="text-sm font-semibold text-[rgb(var(--fg))]">{permission.code}</span>
                                <span className="rounded-full bg-[rgba(var(--brand),0.12)] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.16em] text-[rgb(var(--brand))]">{permission.action}</span>
                              </div>
                              <p className="text-sm text-[rgba(var(--fg),0.78)]">{permission.name}</p>
                              <p className="text-xs text-[rgb(var(--muted))]">{permission.description || 'Sin descripción adicional.'}</p>
                            </div>
                          </label>
                        ))}
                      </div>
                    </details>
                  );
                })}
              </div>
            </div>

            <aside className="rounded-2xl border border-[rgba(var(--border),0.4)] bg-[rgba(var(--panel),0.72)] p-4">
              <h2 className="text-sm font-semibold uppercase tracking-[0.2em] text-[rgb(var(--muted))]">Resumen final</h2>
              <div className="mt-4 flex flex-wrap gap-2">
                {selectedPermissions.map((permission) => (
                  <span key={permission.code} className="rounded-full bg-[rgba(var(--brand),0.12)] px-2.5 py-1 text-xs font-semibold text-[rgb(var(--brand))]">
                    {permission.code}
                  </span>
                ))}
              </div>
            </aside>
          </div>
        </section>

        <StickyActions
          left={(
            <button type="button" onClick={() => navigate('/seguridad/roles')} className="rounded-xl border border-[rgba(var(--border),0.65)] px-4 py-2 text-sm font-semibold text-[rgb(var(--fg))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]">
              Volver
            </button>
          )}
          right={(
            <button type="submit" className="rounded-xl bg-[rgb(var(--brand))] px-5 py-2.5 text-sm font-semibold text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]">
              {isEdit ? 'Guardar cambios' : 'Crear rol'}
            </button>
          )}
        />
      </form>
    </div>
  );
}
